import { useState } from 'react';
import { Box, Flex, Stack, Tabs, Text } from '@chakra-ui/react';

import { getAllPromptKeys, getPromptKeyLabel, PromptKey } from '@/types/enums';

import AnalyzerEditor from './AnalyzerEditor';
import InterviewAnalysisSection from './InterviewAnalysisSection';
import JDExtractorSection from './JDExtractorSection';
import JobRequirementsSection from './JobRequirementsSection';
import OverviewAnalysisSection from './OverviewAnalysisSection';
import ResumeAnalysisSection from './ResumeAnalysisSection';
import ResumeExtractorSection from './ResumeExtractorSection';
import QuestionBankExtractorSection from './QuestionBankExtractorSection';
import VeteransResumeExtractorSection from './VeteransResumeExtractorSection';
import VeteransResumeGenerationSection from './VeteransResumeGenerationSection';
import EmbeddingVectorSection from './EmbeddingVectorSection';
import MilitaryTranslationSampleSection from './MilitaryTranslationSampleSection';

export default function AnalysisIndex() {
  const [section, setSection] = useState<PromptKey>(PromptKey.JDExtractor);
  const [tab, setTab] = useState<string>('edit');

  const handleTabChange = (tab: PromptKey) => {
    setSection(tab);
  };

  return (
    <Flex w="100%" mt="16px" gap="24px" p="24px">
      <Box
        w="240px"
        maxH="calc(100vh - 100px)"
        border="1px solid"
        borderColor="neutrals.200"
        borderRadius="12px"
        p="16px 12px"
      >
        <Stack gap="8px" maxH="calc(100vh - 150px)" overflowY="auto">
          {getAllPromptKeys().map((promptKey) => (
            <Box
              key={promptKey}
              p="16px 12px"
              borderRadius="12px"
              bg={section === promptKey ? 'neutrals.100' : 'transparent'}
              cursor="pointer"
              onClick={() => handleTabChange(promptKey)}
              transition="background-color 0.2s"
            >
              <Text
                color="neutrals.700"
                fontSize="14px"
                fontWeight={section === promptKey ? 'semibold' : 'normal'}
              >
                {getPromptKeyLabel(promptKey)}
              </Text>
            </Box>
          ))}
        </Stack>
      </Box>
      <Stack w="100%" gap="16px">
        <Flex
          alignItems="center"
          h="48px"
          p="16px 24px"
          borderBottom="1px solid"
          borderColor="neutrals.200"
        >
          <Text fontSize="24px" fontWeight="semibold" color="neutrals.700">
            {getPromptKeyLabel(section)}
          </Text>
        </Flex>
        <Box w="100%" border="1px solid" borderColor="neutrals.200" borderRadius="12px">
          <Stack gap="0">
            <Tabs.Root
              value={tab}
              onValueChange={(e) => setTab(e.value)}
              colorPalette="blue"
              p="16px 24px"
            >
              <Tabs.List>
                <Tabs.Trigger value="edit" asChild>
                  <Box fontSize="18px" color={tab === 'edit' ? 'primary.1000' : 'neutrals.900'}>
                    Edit
                  </Box>
                </Tabs.Trigger>
                <Tabs.Trigger value="test" asChild>
                  <Box fontSize="18px" color={tab === 'test' ? 'primary.1000' : 'neutrals.900'}>
                    Test
                  </Box>
                </Tabs.Trigger>
              </Tabs.List>
              <Tabs.Content value="edit">
                {(() => {
                  switch (section) {
                    case PromptKey.EmbeddingVector:
                      return <EmbeddingVectorSection />;
                    case PromptKey.MilitaryTranslationSample:
                      return <MilitaryTranslationSampleSection />;
                    default:
                      return <AnalyzerEditor promptKey={section} />;
                  }
                })()}
              </Tabs.Content>
              <Tabs.Content value="test">
                {(() => {
                  switch (section) {
                    case PromptKey.JDExtractor:
                      return <JDExtractorSection />;
                    case PromptKey.ResumeExtractor:
                      return <ResumeExtractorSection />;
                    case PromptKey.JDRequirements:
                      return <JobRequirementsSection />;
                    case PromptKey.ResumeAnalyzer:
                      return <ResumeAnalysisSection />;
                    case PromptKey.InterviewAnalyzer:
                      return <InterviewAnalysisSection />;
                    case PromptKey.OverviewAnalyzer:
                      return <OverviewAnalysisSection />;
                    case PromptKey.QuestionBankExtractor:
                      return <QuestionBankExtractorSection />;
                    case PromptKey.VeteransResumeExtractor:
                      return <VeteransResumeExtractorSection />;
                    case PromptKey.VeteransResumeGenerator:
                      return <VeteransResumeGenerationSection />;
                    case PromptKey.EmbeddingVector:
                      return <EmbeddingVectorSection />;
                    case PromptKey.MilitaryTranslationSample:
                      return <MilitaryTranslationSampleSection />;
                    default:
                      return null;
                  }
                })()}
              </Tabs.Content>
            </Tabs.Root>
          </Stack>
        </Box>
      </Stack>
    </Flex>
  );
}
